import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  CreateOrganizationDto,
  CreateOrganizationResponseDto,
} from "@workspace/dto/organization/create-organization";
import type { UserMutationResponseDto } from "@workspace/dto/user/mutation-response";
import type { SwitchOrganizationDto } from "@workspace/dto/user/switch-organization";
import { Button } from "@workspace/ui/components/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@workspace/ui/components/form";
import { Input } from "@workspace/ui/components/input";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "@workspace/ui/components/responsive-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@workspace/ui/components/select";
import type { AxiosResponse } from "axios";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Loading } from "@/components/loading";
import { usePilotSelfServeLocked } from "@/components/providers/pilot-flag-provider";
import { refreshSessionFromDatabase } from "@/lib/auth/session-refresh";
import { useStableSession } from "@/lib/auth/use-stable-session";
import { axios } from "@/lib/axios";
import { toastMutationNetworkError } from "@/lib/query-failure";
import { queryKeys } from "@/lib/query-keys";
import {
  CreateOrganizationFormSchema,
  type CreateOrganizationFormValues,
  defaultCreateOrganizationFormValues,
} from "@/lib/schemas/create-organization";
import { settingsPathForEmployerDefault } from "@/lib/settings-navigation";

type Props = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

export function CreateOrganizationModal({ isOpen, onOpenChange }: Props) {
  const pilotSelfServeLocked = usePilotSelfServeLocked();
  const queryClient = useQueryClient();
  const { update } = useStableSession();
  const form = useForm<CreateOrganizationFormValues>({
    resolver: zodResolver(CreateOrganizationFormSchema),
    defaultValues: defaultCreateOrganizationFormValues,
  });

  const createOrganizationMutation = useMutation({
    mutationFn: async (data: Partial<CreateOrganizationDto>) => {
      const response = await axios.post<
        CreateOrganizationResponseDto,
        AxiosResponse<CreateOrganizationResponseDto>,
        Partial<CreateOrganizationDto>
      >("/organizations/create-organization", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.account.organizations,
      });
    },
    onError: (error) => {
      if (toastMutationNetworkError(error, "Couldn't create organization")) {
        return;
      }
      toast.error("Uh oh! Something went wrong.", {
        description: "Failed to create organization",
      });
    },
  });

  const switchOrganizationMutation = useMutation({
    mutationFn: async (data: SwitchOrganizationDto) => {
      const response = await axios.put<
        UserMutationResponseDto,
        AxiosResponse<UserMutationResponseDto>,
        SwitchOrganizationDto
      >("/account/user/switch-organization", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.account.user });
    },
    onError: (error) => {
      if (toastMutationNetworkError(error, "Couldn't switch organization")) {
        return;
      }
      toast.error("Uh oh! Something went wrong.", {
        description: "Failed to switch to the new organization",
      });
    },
  });

  const isPending =
    createOrganizationMutation.isPending ||
    switchOrganizationMutation.isPending;

  const onSubmit = async (values: CreateOrganizationFormValues) => {
    try {
      // react-doctor-disable-next-line react-doctor/async-parallel -- ordered dependent steps: create org → switch to it → refresh session → navigate
      const { organization } =
        await createOrganizationMutation.mutateAsync(values);
      await switchOrganizationMutation.mutateAsync({
        organizationId: organization.id,
      });
      // Refresh the JWT cookie, then hard-nav so the destination page remounts
      // with a clean query cache against the newly active org.
      await refreshSessionFromDatabase(update);
      window.location.assign(settingsPathForEmployerDefault());
    } catch {
      // Toast handled in mutation onError.
    }
  };

  const _onOpenChange = (open: boolean) => {
    onOpenChange(open);
    form.reset();
  };

  if (pilotSelfServeLocked) {
    return null;
  }

  return (
    <ResponsiveDialog onOpenChange={_onOpenChange} open={isOpen}>
      <ResponsiveDialogContent className="max-w-[455px]">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>Create organization</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            For example, you can use the name of your company or department.
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <Form {...form}>
          <form
            className="flex flex-col space-y-4"
            id="modals/jobs/create-organization"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      autoCapitalize="none"
                      autoComplete="off"
                      autoCorrect="off"
                      autoFocus
                      placeholder="Ex: Acme Marketing or Acme Co"
                      spellCheck="false"
                      {...field}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <span className="flex items-start space-x-2">
                    <Select
                      defaultValue={field.value}
                      onValueChange={field.onChange}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="personal">Personal</SelectItem>
                        <SelectItem value="business">Business</SelectItem>
                      </SelectContent>
                    </Select>
                  </span>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="industry"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Industry</FormLabel>
                  <FormControl>
                    <Input
                      autoCapitalize="none"
                      autoComplete="off"
                      autoCorrect="off"
                      autoFocus
                      placeholder="Ex: Restaurants"
                      spellCheck="false"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
        <ResponsiveDialogFooter>
          <div className="flex flex-row justify-end">
            <Button
              disabled={isPending}
              form="modals/jobs/create-organization"
              type="submit"
            >
              {isPending ? <Loading /> : <span>Create</span>}
            </Button>
          </div>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
